import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck } from 'lucide-react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { AdSenseSlot } from '../components/AdSenseSlot';
import { guideMap, guides } from '../data/guides';
import { toolMap } from '../data/tools';
import { breadcrumbSchema, Seo } from '../lib/seo';
import { absoluteUrl } from '../lib/site';
import demoBefore from '../assets/hero-before.png';
import demoAfter from '../assets/hero-after.png';

function guideDateToIso(value: string) {
  const parsed = new Date(`${value} 00:00:00 UTC`);
  return Number.isNaN(parsed.getTime()) ? '2026-07-19' : parsed.toISOString().slice(0, 10);
}

function sectionId(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export function GuideDetailPage() {
  const { slug } = useParams();
  const guide = slug ? guideMap[slug] : undefined;
  if (!guide) return <Navigate to="/guides" replace />;
  const RelatedIcon = guide.icon;
  const datePublished = guide.published ?? '2026-07-17';
  const dateModified = guideDateToIso(guide.updated);
  const tool = toolMap[guide.relatedTool];
  const related = guides
    .filter((item) => item.slug !== guide.slug)
    .sort((a, b) => {
      const aScore = Number(a.relatedTool === guide.relatedTool) * 2 + Number(a.category === guide.category);
      const bScore = Number(b.relatedTool === guide.relatedTool) * 2 + Number(b.category === guide.category);
      return bScore - aScore;
    })
    .slice(0, 3);

  const wordCount = [guide.intro, ...guide.takeaways, ...guide.sections.flatMap((section) => [section.heading, ...section.paragraphs, ...(section.bullets ?? []), section.note ?? ''])]
    .join(' ')
    .trim()
    .split(/\s+/)
    .length;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.seoDescription,
    image: absoluteUrl('/og-nexhance-ai.png'),
    articleSection: guide.category,
    keywords: guide.keywords?.join(', '),
    wordCount,
    inLanguage: 'en',
    isAccessibleForFree: true,
    datePublished,
    dateModified,
    mainEntityOfPage: absoluteUrl(`/guides/${guide.slug}`),
    author: { '@type': 'Organization', name: 'Nexhance AI', url: absoluteUrl('/about') },
    publisher: { '@type': 'Organization', name: 'Nexhance AI', url: absoluteUrl('/'), logo: { '@type': 'ImageObject', url: absoluteUrl('/favicon-256x256.png') } }
  };

  return (
    <article className="page-section guide-detail-page">
      <Seo
        title={guide.seoTitle}
        description={guide.seoDescription}
        path={`/guides/${guide.slug}`}
        type="article"
        publishedTime={datePublished}
        modifiedTime={dateModified}
        imageAlt={`${guide.shortTitle} guide from Nexhance AI`}
        jsonLd={[
          articleSchema,
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Guides', path: '/guides' },
            { name: guide.shortTitle, path: `/guides/${guide.slug}` }
          ])
        ]}
      />
      <div className="shell guide-detail-shell">
        <Link to="/guides" className="guide-back"><ArrowLeft size={17} /> All guides</Link>

        <header className="guide-detail-hero">
          <span className="large-tool-icon"><RelatedIcon size={30} /></span>
          <p className="eyebrow">{guide.category}</p>
          <h1>{guide.title}</h1>
          <p>{guide.description}</p>
          <div className="guide-detail-meta">
            <span>Updated {guide.updated}</span>
            <span><ShieldCheck size={16} /> Written and maintained by <Link to="/about">Nexhance AI</Link></span>
          </div>
          {guide.keywords?.length ? <div className="guide-keyword-row" aria-label="Topics covered">{guide.keywords.slice(0, 6).map((keyword) => <span key={keyword}>{keyword}</span>)}</div> : null}
        </header>

        <div className="guide-article-layout">
          <aside className="guide-takeaways">
            <p className="eyebrow">Key takeaways</p>
            {guide.takeaways.map((takeaway) => <span key={takeaway}><CheckCircle2 size={16} /> {takeaway}</span>)}
            <nav className="guide-toc" aria-label="On this page">
              <strong>On this page</strong>
              {guide.sections.map((section) => <a key={section.heading} href={`#${sectionId(section.heading)}`}>{section.heading}</a>)}
            </nav>
            <Link className="button button-primary" to={`/tools/${tool.slug}`}>Try {tool.shortName} <ArrowRight size={16} /></Link>
          </aside>

          <div className="guide-article-body">
            <p className="guide-lead">{guide.intro}</p>

            {guide.slug === 'enhance-image-quality-without-overprocessing' && (
              <figure className="guide-visual-review-example">
                <div className="guide-visual-review-images">
                  <div><span>Illustrative source</span><img src={demoBefore} alt="Soft portrait example used to demonstrate before-and-after inspection" loading="lazy" /></div>
                  <div><span>Illustrative enhanced view</span><img src={demoAfter} alt="Detailed portrait example used to demonstrate before-and-after inspection" loading="lazy" /></div>
                </div>
                <figcaption><strong>How to review a before-and-after:</strong> inspect eyes, hair, skin texture, repeated patterns and high-contrast edges at 100% zoom, then check the image again at its real delivery size. These are existing Nexhance AI demo assets used to explain the review method; they are not a guarantee that every upload will produce the same level of change.</figcaption>
              </figure>
            )}
            {guide.sections.map((section, index) => (
              <div key={section.heading}>
                <section id={sectionId(section.heading)} className="guide-content-section">
                  <h2>{section.heading}</h2>
                  {section.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                  {section.bullets && <ul>{section.bullets.map((bullet) => <li key={bullet}>{bullet}</li>)}</ul>}
                  {section.note && <div className="guide-note">{section.note}</div>}
                </section>
                {index === 1 && <AdSenseSlot slot={String(import.meta.env.VITE_ADSENSE_CONTENT_SLOT ?? '')} />}
              </div>
            ))}

            <div className="guide-editorial-note">
              <strong>Editorial approach</strong>
              <p>Method: identify the visible source defect, preserve the original, use the smallest workflow that meets the delivery requirement, and compare high-risk details at 100% zoom and at final output size. Guidance is updated when Nexhance AI controls, supported formats, output limits or processing behavior change. Results vary by source quality, and reconstructed details should not be treated as facts that were never recorded in the source.</p>
              <Link to="/methodology" className="inline-link">Read our editorial and image-review methodology</Link>
            </div>

            <div className="guide-tool-cta">
              <div>
                <p className="eyebrow">Try the matching workflow</p>
                <h2>{tool.name}</h2>
                <p>{tool.description}</p>
              </div>
              <Link className="button button-primary button-large" to={`/tools/${tool.slug}`}>Start with {tool.shortName} <ArrowRight size={18} /></Link>
            </div>
          </div>
        </div>

        <section className="related-guides">
          <div className="section-heading"><p className="eyebrow">Continue learning</p><h2>Related image guides</h2></div>
          <div className="related-guide-grid">
            {related.map((item) => (
              <Link key={item.slug} to={`/guides/${item.slug}`}>
                <span>{item.category}</span>
                <strong>{item.title}</strong>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </article>
  );
}
